import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Database, AlertCircle, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import AhjContactsManager from "@/components/AhjContactsManager";
import AhjBackfillPanel from "@/components/AhjBackfillPanel";
import XlsxIngestPanel from "@/components/XlsxIngestPanel";
import ChunkedMboxUploader from "@/components/ChunkedMboxUploader";

export default function AdminBPS() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/map");
      return;
    }
    if (user) loadStatus();
  }, [user, navigate]);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/bps/monthly/status");
      setStatus(data);
    } catch (e) {
      // ignore
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/bps/monthly", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Ingested ${data.rows_ingested} rows from ${data.states} state(s)`);
      loadStatus();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-bps-page">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Admin · Data ingestion</div>
        <h1 className="font-outfit text-4xl font-bold tracking-tight text-slate-900">BPS Monthly Upload</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Upload Census state-level monthly permit data (CSV). Backend apportions to counties by their annual share — replacing region-aware estimates with real numbers per state where available.
        </p>

        <div className="mt-8 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div>
              <h3 className="font-outfit text-lg font-semibold text-slate-900">Upload CSV</h3>
              <p className="text-xs text-slate-500 mt-1">
                Required columns: <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">state_fips, year, month, buildings, units, value</code>
              </p>
            </div>
            <label className="cursor-pointer">
              <input
                type="file" accept=".csv,text/csv" onChange={handleUpload} disabled={uploading}
                className="hidden" data-testid="bps-upload-input"
              />
              <Button asChild data-testid="bps-upload-btn" disabled={uploading} className="bg-slate-900 hover:bg-slate-800 text-white pointer-events-none">
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading…" : "Choose CSV file"}
                </span>
              </Button>
            </label>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-none mt-0.5" />
            <div>
              <strong>Where to get the CSV:</strong> Download monthly state files from{" "}
              <a href="https://www.census.gov/construction/bps/" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                census.gov/construction/bps
              </a>{" "}
              and convert to the expected schema (state_fips, year, month, buildings, units, value). One row per state per month.
            </div>
          </div>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="bps-status-card">
          <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Database className="w-4 h-4 text-slate-500" /> Current data
          </h3>
          {status && status.total_rows > 0 ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-slate-900"><strong>{status.total_rows}</strong> rows stored across <strong>{status.by_year.length}</strong> year(s)</span>
              </div>
              <div className="space-y-1.5">
                {status.by_year.map((y) => (
                  <div key={y.year} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0" data-testid={`year-row-${y.year}`}>
                    <span className="font-mono text-sm text-slate-900">{y.year}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-[10px]">{y.states} states</Badge>
                      <Badge variant="outline" className="font-mono text-[10px]">{y.rows} rows</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-500" data-testid="bps-no-data">No monthly data uploaded yet. Counties use regional-seasonality estimates.</div>
          )}
        </div>

        {/* AHJ Intel bulk CSV */}
        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="ahj-csv-card">
          <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-2">AHJ Intel — Bulk CSV upload</h3>
          <p className="text-xs text-slate-500 mb-4">
            Required columns: <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">key, jurisdiction, portal, process_notes</code>.
            Optional: <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">contact_email, status_keyword, last_seen, source_client, scope</code>.
            `key` is county FIPS (5 digits) or place GEOID (7 digits).
          </p>
          <label className="cursor-pointer">
            <input type="file" accept=".csv" data-testid="ahj-csv-input" className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                const fd = new FormData(); fd.append("file", file);
                try {
                  const { data } = await api.post("/admin/ahj/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success(`Ingested ${data.rows_ingested} AHJ entries`);
                } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
                e.target.value = "";
              }} />
            <Button asChild data-testid="ahj-csv-btn" className="bg-slate-900 hover:bg-slate-800 text-white pointer-events-none">
              <span><Upload className="w-4 h-4 mr-2" />Upload AHJ CSV</span>
            </Button>
          </label>
        </div>

        {/* Mbox parser — chunked uploader for large Gmail Takeouts */}
        <ChunkedMboxUploader />

        {/* Back-fill panel — jurisdiction inference upgrades for email contacts */}
        <AhjBackfillPanel />

        {/* Xlsx legacy ingest — full inline panel with sortable flagged table */}
        <XlsxIngestPanel />

        {/* Contacts manager — view / filter / drill-down / delete */}
        <AhjContactsManager />
      </main>
    </div>
  );
}
